import React from 'react';
import { DEFAULT_THEME_COLOR } from 'appenv';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import TimeZoneSwitch from 'components/TimeZoneSwitch';
import { useSelector } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import moment from 'moment-timezone';
import { selectLocalizationState } from 'models/localization';

import { BREAKPOINT } from './constants';

const RootContainer = styled.div`
  flex: 0 0 auto;
  display: flex;
  position: relative;
  width: 100%;
  border-bottom: 2px solid #DBDEE2;

  @media (max-width: ${BREAKPOINT}px) {
    z-index: 50;
  }
`;

const FlexGrow = styled.div`
  width: ${({ $sameTimeZone }) => ($sameTimeZone ? '100%' : 'calc(100% - 200px)')};
  position: relative;

  @media (max-width: 959px) {
    width: 100%;
  }
`;

const StyledTabsContainer = ({ children, ...props }) => {
  const { themeColor } = useSelector((s) => s.eventConfigs);
  const CustomTabs = withStyles(() => ({
    root: {
      flexShrink: 0,
      [`@media (max-width: ${BREAKPOINT}px)`]: {
        backgroundColor: '#FFF',
      },
    },
    flexContainer: {
      borderBottom: '2px solid #DBDEE2',
    },
    indicator: {
      backgroundColor: themeColor || DEFAULT_THEME_COLOR,
      height: '4px',
    },
  }))(Tabs);
  return <CustomTabs {...props}>{children}</CustomTabs>;
};
StyledTabsContainer.propTypes = {
  children: PropTypes.node,
};
StyledTabsContainer.defaultProps = {
  children: null,
};

const StyledTab = ({ children, ...props }) => {
  const { themeColor } = useSelector((s) => s.eventConfigs);
  const Custom = withStyles(() => ({
    root: {
      color: '#A2AAB3',
      fontSize: '18px',
      fontWeight: 'bold',
      textTransform: 'none',
    },
    selected: {
      color: themeColor || DEFAULT_THEME_COLOR,
    },
  }))(Tab);
  return <Custom {...props}>{children}</Custom>;
};
StyledTab.propTypes = {
  children: PropTypes.node,
};
StyledTab.defaultProps = {
  children: null,
};

const StyledLocalTimeSwitchLabel = styled(TimeZoneSwitch)`
  width: 200px;
  display: flex;
  justify-content: flex-end;
  @media (max-width: 959px) {
    display: none;
  }
`;

const DaySelector = ({
  days,
  activeIndex,
  onDaySelected,
}) => {
  const { eventTimezone } = useSelector(createSelector(selectLocalizationState,
    ((state) => ({ useLocalTime: state.useLocalTime, eventTimezone: state.eventTimezone }))));

  const sameTimeZone = moment.tz(moment.tz.guess()).utcOffset() === moment.tz(eventTimezone).utcOffset();

  return (
    <RootContainer>
      <FlexGrow $sameTimeZone={sameTimeZone}>
        <StyledTabsContainer
          value={activeIndex}
          onChange={(_, selectedIndex) => {
            onDaySelected(selectedIndex);
          }}
          variant="scrollable"
          scrollButtons="auto"
        >
          {
            days.map((day, index) => (
              <StyledTab
                key={index}
                label={day}
              />
            ))
          }
        </StyledTabsContainer>
      </FlexGrow>
      <StyledLocalTimeSwitchLabel />
    </RootContainer>
  );
};

DaySelector.propTypes = {
  days: PropTypes.arrayOf(PropTypes.string),
  activeIndex: PropTypes.number,
  onDaySelected: PropTypes.func,
};

DaySelector.defaultProps = {
  days: [],
  activeIndex: 0,
  onDaySelected: () => {
  },
};

export default DaySelector;
